import { useCallback, useEffect, useMemo, useState } from "react";
import { PageHeader } from "../components/PageHeader";
import { RecursiveEditor } from "../components/RecursiveEditor";
import { InlineError, LoadingState } from "../components/States";
import { useToast } from "../components/Toast";
import { adminApi } from "../lib/api";
import {
  enforceAtalayBrand,
  findProhibitedTerms,
  isPlainObject,
  stableJson
} from "../lib/content";
import type { ContentResponse, JsonValue } from "../types";

function versionLabel(version: JsonValue, index: number): string {
  if (version && typeof version === "object" && !Array.isArray(version)) {
    const record = version as Record<string, JsonValue>;
    const timestamp = record.publishedAt || record.createdAt || record.timestamp;
    if (typeof timestamp === "string" && Number.isFinite(Date.parse(timestamp))) {
      return new Intl.DateTimeFormat("en", {
        dateStyle: "medium",
        timeStyle: "short"
      }).format(new Date(timestamp));
    }
    if (typeof record.version === "string" || typeof record.version === "number") {
      return `Version ${record.version}`;
    }
  }
  return `Version ${index + 1}`;
}

export function ContentPage() {
  const { showToast } = useToast();
  const [response, setResponse] = useState<ContentResponse | null>(null);
  const [draft, setDraft] = useState<JsonValue | null>(null);
  const [savedSnapshot, setSavedSnapshot] = useState("");
  const [search, setSearch] = useState("");
  const [mode, setMode] = useState<"structured" | "json">("structured");
  const [jsonText, setJsonText] = useState("");
  const [jsonError, setJsonError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const nextResponse = await adminApi.content();
      const source =
        nextResponse.draft ?? nextResponse.published ?? ({} as JsonValue);
      const nextDraft = enforceAtalayBrand(source);
      setResponse(nextResponse);
      setDraft(nextDraft);
      setSavedSnapshot(stableJson(nextDraft));
      setJsonText(JSON.stringify(nextDraft, null, 2));
      setJsonError("");
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "Website content could not be loaded."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const warnings = useMemo(
    () => (draft === null ? [] : findProhibitedTerms(draft)),
    [draft]
  );
  const hasUnsaved = draft !== null && stableJson(draft) !== savedSnapshot;
  const publishedInSync =
    draft !== null &&
    response?.published !== undefined &&
    stableJson(draft) === stableJson(enforceAtalayBrand(response.published));
  const normalizedJson = draft === null ? "" : JSON.stringify(draft, null, 2);
  const hasPendingJson = mode === "json" && jsonText !== normalizedJson;

  useEffect(() => {
    const dirty = hasUnsaved || hasPendingJson;
    window.dispatchEvent(new CustomEvent("atalay:dirty-state", { detail: dirty }));
    function beforeUnload(event: BeforeUnloadEvent) {
      if (!dirty) return;
      event.preventDefault();
    }
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [hasPendingJson, hasUnsaved]);

  useEffect(
    () => () => {
      window.dispatchEvent(
        new CustomEvent("atalay:dirty-state", { detail: false })
      );
    },
    []
  );

  function handleEditorChange(next: JsonValue) {
    const branded = enforceAtalayBrand(next);
    setDraft(branded);
    if (mode === "json") setJsonText(JSON.stringify(branded, null, 2));
  }

  function changeMode(nextMode: "structured" | "json") {
    if (mode === "json" && nextMode === "structured" && hasPendingJson) {
      if (!window.confirm("Discard unapplied JSON edits and return to the structured editor?")) {
        return;
      }
    }
    setMode(nextMode);
    if (nextMode === "json" && draft !== null) {
      setJsonText(JSON.stringify(draft, null, 2));
      setJsonError("");
    }
  }

  function applyJson() {
    try {
      const parsed = JSON.parse(jsonText) as JsonValue;
      if (!isPlainObject(parsed)) {
        throw new Error("The website content must be a JSON object.");
      }
      const branded = enforceAtalayBrand(parsed);
      setDraft(branded);
      setJsonText(JSON.stringify(branded, null, 2));
      setJsonError("");
      showToast("JSON is valid and has been applied.", "success");
    } catch (parseError) {
      setJsonError(
        parseError instanceof Error ? parseError.message : "Invalid JSON."
      );
    }
  }

  async function saveDraft() {
    if (draft === null || jsonError || hasPendingJson) return;
    setSaving(true);
    try {
      const branded = enforceAtalayBrand(draft);
      await adminApi.saveDraft(branded);
      setDraft(branded);
      setSavedSnapshot(stableJson(branded));
      showToast("Draft saved.", "success");
    } catch (requestError) {
      showToast(
        requestError instanceof Error ? requestError.message : "Draft save failed.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  }

  async function publish() {
    if (draft === null || jsonError || hasPendingJson) return;
    if (warnings.length > 0) {
      showToast("Remove prohibited marketing claims before publishing.", "error");
      document.getElementById("content-warnings")?.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
      return;
    }
    if (
      !window.confirm(
        "Publish this content to the live English and Turkish website? This creates a new published version."
      )
    ) {
      return;
    }
    setPublishing(true);
    try {
      const branded = enforceAtalayBrand(draft);
      await adminApi.publish(branded);
      setResponse((current) =>
        current ? { ...current, draft: branded, published: branded } : current
      );
      setSavedSnapshot(stableJson(branded));
      showToast("Website content published.", "success");
    } catch (requestError) {
      showToast(
        requestError instanceof Error ? requestError.message : "Publish failed.",
        "error"
      );
    } finally {
      setPublishing(false);
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="English + Türkçe"
        title="Content"
        description="Edit page copy and structured lists. Brand fields are fixed to ATALAY."
        actions={
          <div className="segmented" aria-label="Editor mode">
            <button
              type="button"
              className={mode === "structured" ? "is-active" : ""}
              aria-pressed={mode === "structured"}
              onClick={() => changeMode("structured")}
            >
              Structured
            </button>
            <button
              type="button"
              className={mode === "json" ? "is-active" : ""}
              aria-pressed={mode === "json"}
              onClick={() => changeMode("json")}
            >
              JSON
            </button>
          </div>
        }
      />

      {loading ? (
        <LoadingState label="Loading website content" />
      ) : error ? (
        <InlineError message={error} retry={() => void load()} />
      ) : draft === null ? (
        <InlineError message="The API returned no editable content." />
      ) : (
        <>
          <section className="content-status" aria-label="Content status">
            <div>
              <span className={`status-badge ${hasUnsaved ? "status-badge--warning" : "status-badge--success"}`}>
                {hasUnsaved ? "Unsaved changes" : "Draft saved"}
              </span>
              <span className="status-separator" aria-hidden="true">/</span>
              <span className={`status-badge ${publishedInSync ? "status-badge--success" : ""}`}>
                {publishedInSync ? "Draft is live" : "Draft differs from live"}
              </span>
            </div>
            <span>
              {response?.versions?.length || 0} published{" "}
              {(response?.versions?.length || 0) === 1 ? "version" : "versions"}
            </span>
          </section>

          {warnings.length > 0 && (
            <section className="warning-panel" id="content-warnings" role="alert">
              <div>
                <p className="eyebrow">Publishing check</p>
                <h2>Remove prohibited or unverified claims</h2>
                <p>
                  Drafts can be saved, but publishing is paused until these phrases
                  are removed or replaced with factual language.
                </p>
              </div>
              <ul>
                {warnings.slice(0, 12).map((warning, index) => (
                  <li key={`${warning.path}-${warning.term}-${index}`}>
                    <strong>“{warning.term}”</strong> in {warning.path}
                    <small>{warning.excerpt}</small>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {mode === "structured" ? (
            <>
              <div className="editor-tools">
                <div className="field search-field">
                  <label htmlFor="content-search">Search fields and copy</label>
                  <input
                    id="content-search"
                    type="search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Try “hero”, “contact”, or a phrase"
                  />
                </div>
                {search && (
                  <button
                    type="button"
                    className="text-button"
                    onClick={() => setSearch("")}
                  >
                    Clear search
                  </button>
                )}
              </div>
              <RecursiveEditor
                root={draft}
                search={search}
                onChange={handleEditorChange}
              />
            </>
          ) : (
            <section className="json-editor">
              <div className="json-editor__heading">
                <div>
                  <h2>Advanced JSON</h2>
                  <p>
                    Changes are not saved until the JSON is valid and applied.
                    Brand values are normalized to ATALAY.
                  </p>
                </div>
                <button type="button" className="button" onClick={applyJson}>
                  Validate &amp; apply
                </button>
              </div>
              <label className="sr-only" htmlFor="content-json">
                Complete website content JSON
              </label>
              <textarea
                id="content-json"
                className="code-editor"
                value={jsonText}
                spellCheck={false}
                aria-invalid={Boolean(jsonError)}
                aria-describedby={jsonError ? "json-error" : undefined}
                onChange={(event) => {
                  setJsonText(event.target.value);
                  setJsonError("");
                }}
              />
              {jsonError && (
                <p className="field__error" id="json-error" role="alert">
                  {jsonError}
                </p>
              )}
              {hasPendingJson && !jsonError && (
                <p className="field__help">Unapplied JSON changes.</p>
              )}
            </section>
          )}

          {Boolean(response?.versions?.length) && (
            <details className="version-history">
              <summary>Published version history</summary>
              <ol>
                {response!.versions!.slice(0, 10).map((version, index) => (
                  <li key={index}>
                    <span>{versionLabel(version, index)}</span>
                    {index === 0 && <small>Most recent</small>}
                  </li>
                ))}
              </ol>
            </details>
          )}

          <div className="save-bar">
            <div>
              <strong>
                {hasPendingJson
                  ? "Apply JSON before saving"
                  : hasUnsaved
                    ? "Changes are not saved yet"
                    : "Draft is saved"}
              </strong>
              <small>Publishing updates both language versions together.</small>
            </div>
            <div className="save-bar__actions">
              <button
                type="button"
                className="button"
                disabled={saving || publishing || !hasUnsaved || hasPendingJson}
                onClick={() => void saveDraft()}
              >
                {saving ? "Saving…" : "Save draft"}
              </button>
              <button
                type="button"
                className="button button--primary"
                disabled={saving || publishing || hasPendingJson}
                onClick={() => void publish()}
              >
                {publishing ? "Publishing…" : "Publish"}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
